import { useState } from "react";
import { Eye, History } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_COLORS = { PENDING_STORE_RECEIPT: "bg-amber-500", RECEIVED_AT_STORE: "bg-blue-600", RETURN_DETAILS_COMPLETED: "bg-emerald-600" };

export default function OldMeterReturnRegister() {
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState([]);

  const load = (params) => api.get("/old-meter-returns", { params: { ...params, ...(pid ? { project_id: pid } : {}) } }).then((r) => r.data);
  const open = async (row) => {
    const { data } = await api.get(`/old-meter-returns/${row.id}`); setDetail(data);
    const { data: tl } = await api.get(`/old-meter-returns/${row.id}/timeline`); setTimeline(tl.items || []);
  };

  const columns = [
    { key: "old_meter_no", label: "Old Meter", mono: true },
    { key: "consumer_number", label: "Consumer", mono: true, render: (r) => r.consumer_number || "—" },
    { key: "consumer_name", label: "Name", hideMobile: true, render: (r) => r.consumer_name || "—" },
    { key: "new_meter_serial", label: "New Meter", mono: true, hideMobile: true, render: (r) => r.new_meter_serial || "—" },
    { key: "data_entry_number", label: "Source Entry", mono: true, hideMobile: true },
    { key: "receiving_store_name", label: "Store", render: (r) => r.receiving_store_name || "—" },
    { key: "actual_receipt_date", label: "Receipt", hideMobile: true, render: (r) => r.actual_receipt_date || "—" },
    { key: "status", label: "Status", render: (r) => <Badge className={STATUS_COLORS[r.status] || "bg-slate-500"}>{(r.status || "").replace(/_/g, " ")}</Badge> },
  ];
  const filters = [
    { key: "status", label: "Status", options: [{ value: "PENDING_STORE_RECEIPT", label: "Pending Receipt" }, { value: "RECEIVED_AT_STORE", label: "Received" }, { value: "RETURN_DETAILS_COMPLETED", label: "Completed" }] },
  ];

  return (
    <div>
      <PageHeader title="Old Meter Return Register" subtitle="All old meter return obligations & their lifecycle" />
      <ProjectScopeBanner />
      <DataTable testid="omr-register" columns={columns} load={load} filters={filters} refreshKey={pid || "all"} searchPlaceholder="Search old meter / consumer / entry..."
        rowActions={(row) => <Button data-testid={`omr-reg-view-${row.id}`} variant="ghost" size="sm" onClick={() => open(row)}><Eye className="h-4 w-4" /></Button>} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}><DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-mono">Old Meter {detail?.old_meter_no}</DialogTitle></DialogHeader>
        {detail && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge className={STATUS_COLORS[detail.status] || "bg-slate-500"}>{(detail.status || "").replace(/_/g, " ")}</Badge>
              {detail.return_details_status && <Badge variant="outline">Details: {detail.return_details_status}</Badge>}
              {detail.return_form_version_id && <Badge variant="outline">Return Form bound</Badge>}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Info label="Consumer" value={`${detail.consumer_number || "—"} ${detail.consumer_name ? "· " + detail.consumer_name : ""}`} />
              <Info label="New Meter" value={detail.new_meter_serial} />
              <Info label="Replacement Date" value={detail.installation_date} />
              <Info label="Source Data Entry" value={detail.data_entry_number} />
              <Info label="Receiving Store" value={detail.receiving_store_name} />
              <Info label="Actual Receipt Date" value={detail.actual_receipt_date} />
              <Info label="System Received At" value={(detail.system_received_at || "").slice(0, 19).replace("T", " ")} />
              <Info label="Received By" value={detail.received_by_name} />
            </div>
            <div className="rounded-lg border p-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-slate-400"><History className="h-3.5 w-3.5" /> Timeline</div>
              {timeline.map((e) => (
                <div key={e.id} className="flex items-start justify-between border-b py-1.5 last:border-0" data-testid={`omr-event-${e.event}`}>
                  <div><div className="font-medium text-slate-700">{(e.event || "").replace(/_/g, " ")}</div><div className="text-xs text-slate-400">{e.actor_name || e.actor_email}</div></div>
                  <div className="text-right text-xs text-slate-400">{(e.server_ts || "").slice(0, 19).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent></Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-slate-800">{value || "—"}</div></div>;
}
